import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Megaphone, ReceiptText, RefreshCw, Store, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { AdminOverviewResponse } from '@agenda-facil/contracts';
import { Button, Surface } from '@agenda-facil/ui';
import { ErrorState } from '../../components/query-feedback';
import { currency, shortDate, shortDateTime } from '../../features/admin/admin-format';
import { AdminStatusPill } from '../../features/admin/admin-status-pill';
import { api } from '../../lib/api';
import { AdminPageHeader } from './admin-page-header';

export function AdminDashboardPage() {
  const overview = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: () => api.get<AdminOverviewResponse>('/admin/overview'),
  });

  if (overview.isLoading) return <AdminDashboardSkeleton />;
  if (overview.isError || !overview.data) {
    return (
      <ErrorState
        message="Não foi possível carregar os indicadores da plataforma."
        retry={() => void overview.refetch()}
      />
    );
  }

  const { metrics, recentShops, generatedAt } = overview.data;
  const primaryMetrics = [
    {
      label: 'Barbearias',
      value: metrics.totalShops.toLocaleString('pt-BR'),
      note: `${metrics.newShopsLast30Days} novas em 30 dias`,
    },
    {
      label: 'Testes ativos',
      value: metrics.activeTrials.toLocaleString('pt-BR'),
      note: 'Períodos ainda vigentes',
    },
    {
      label: 'Assinaturas ativas',
      value: metrics.activeSubscriptions.toLocaleString('pt-BR'),
      note: `${metrics.pastDueSubscriptions} em atraso`,
    },
    {
      label: 'Receita recorrente',
      value: currency.format(metrics.monthlyRecurringRevenueCents / 100),
      note: 'Estimativa mensal atual',
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title="Visão geral"
        description={`Indicadores consolidados até ${shortDateTime.format(new Date(generatedAt))}.`}
        action={
          <Button
            variant="secondary"
            onClick={() => void overview.refetch()}
            disabled={overview.isFetching}
          >
            <RefreshCw className={overview.isFetching ? 'animate-spin' : ''} size={17} />
            Atualizar
          </Button>
        }
      />

      <Surface className="mt-8 overflow-hidden">
        <div className="grid gap-px bg-[var(--border)] sm:grid-cols-2 xl:grid-cols-4">
          {primaryMetrics.map((metric) => (
            <div key={metric.label} className="bg-[var(--surface)] px-5 py-6 sm:px-6">
              <p className="text-xs font-extrabold uppercase tracking-[.08em] text-[var(--muted-foreground)]">
                {metric.label}
              </p>
              <p className="mt-3 text-3xl font-black tracking-[-.045em]">{metric.value}</p>
              <p className="mt-2 text-xs text-[var(--muted-foreground)]">{metric.note}</p>
            </div>
          ))}
        </div>
      </Surface>

      <div className="mt-6 grid gap-6 xl:grid-cols-[.78fr_1.22fr]">
        <Surface className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-black tracking-[-.025em]">Saúde comercial</h2>
              <p className="mt-1 text-sm text-[var(--muted-foreground)]">
                Itens que podem exigir acompanhamento.
              </p>
            </div>
            <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-[var(--surface-raised)] text-[var(--muted-foreground)]">
              <TrendingUp size={19} />
            </span>
          </div>
          <div className="mt-5 divide-y divide-[var(--border)] border-y border-[var(--border)]">
            <HealthRow
              icon={ReceiptText}
              label="Pagamentos pendentes"
              value={metrics.pendingPayments}
              detail="Cobranças aguardando confirmação"
            />
            <HealthRow
              icon={Megaphone}
              label="Campanhas ativas"
              value={metrics.activeCampaigns}
              detail="Anúncios em veiculação"
            />
            <HealthRow
              icon={Store}
              label="Cancelamentos em 30 dias"
              value={metrics.canceledSubscriptionsLast30Days}
              detail="Assinaturas canceladas no período"
            />
          </div>
        </Surface>

        <Surface className="overflow-hidden">
          <div className="flex items-center justify-between gap-4 px-5 py-5 sm:px-6">
            <div>
              <h2 className="text-lg font-black tracking-[-.025em]">Cadastros recentes</h2>
              <p className="mt-1 text-sm text-[var(--muted-foreground)]">
                Últimas barbearias que entraram na plataforma.
              </p>
            </div>
            <Button asChild variant="ghost" size="sm">
              <Link to="/admin/barbearias">
                Ver todas <ArrowRight size={16} />
              </Link>
            </Button>
          </div>
          {recentShops.length > 0 ? (
            <div className="border-t border-[var(--border)]">
              {recentShops.map((shop) => (
                <div
                  key={shop.id}
                  className="flex flex-col gap-3 border-b border-[var(--border)] px-5 py-4 last:border-b-0 sm:flex-row sm:items-center sm:justify-between sm:px-6"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-[var(--accent-soft)] text-sm font-black text-[var(--accent)]">
                      {shop.name.slice(0, 1).toUpperCase()}
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-extrabold">{shop.name}</p>
                      <p className="truncate text-xs text-[var(--muted-foreground)]">
                        {shop.ownerName} · {shortDate.format(new Date(shop.createdAt))}
                      </p>
                    </div>
                  </div>
                  <AdminStatusPill status={shop.status} />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid min-h-56 place-items-center border-t border-[var(--border)] px-6 text-center">
              <div className="max-w-sm">
                <Store className="mx-auto text-[var(--muted-foreground)]" size={28} />
                <h3 className="mt-4 font-extrabold">Nenhuma barbearia cadastrada</h3>
                <p className="mt-2 text-sm leading-6 text-[var(--muted-foreground)]">
                  Os novos testes aparecerão aqui assim que o primeiro proprietário concluir o
                  cadastro.
                </p>
              </div>
            </div>
          )}
        </Surface>
      </div>
    </div>
  );
}

function HealthRow({
  icon: Icon,
  label,
  value,
  detail,
}: {
  icon: typeof Store;
  label: string;
  value: number;
  detail: string;
}) {
  return (
    <div className="flex items-center gap-3 py-4">
      <Icon className="shrink-0 text-[var(--muted-foreground)]" size={18} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-extrabold">{label}</p>
        <p className="mt-0.5 text-xs text-[var(--muted-foreground)]">{detail}</p>
      </div>
      <span className="text-xl font-black tabular-nums">{value}</span>
    </div>
  );
}

function AdminDashboardSkeleton() {
  return (
    <div className="animate-pulse" aria-label="Carregando visão geral">
      <div className="h-4 w-36 rounded bg-[var(--surface-raised)]" />
      <div className="mt-3 h-10 w-64 rounded bg-[var(--surface-raised)]" />
      <div className="mt-8 grid gap-px overflow-hidden rounded-[var(--radius-lg)] bg-[var(--border)] sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((item) => (
          <div key={item} className="h-36 bg-[var(--surface)] p-6">
            <div className="h-3 w-24 rounded bg-[var(--surface-raised)]" />
            <div className="mt-5 h-8 w-28 rounded bg-[var(--surface-raised)]" />
          </div>
        ))}
      </div>
      <div className="mt-6 grid gap-6 xl:grid-cols-2">
        <div className="h-80 rounded-[var(--radius-lg)] bg-[var(--surface-raised)]" />
        <div className="h-80 rounded-[var(--radius-lg)] bg-[var(--surface-raised)]" />
      </div>
    </div>
  );
}
